/**
 * Stats cards component
 * Displays KPI metrics on the dashboard
 */

import { Row, Col, Card, Statistic, Tooltip } from 'antd';
import {
  DatabaseOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import type { JobStats } from '@/types/job';
import { formatNumber } from '@/utils/formatters';

interface StatsCardsProps {
  stats?: JobStats;
  loading?: boolean;
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  suffix?: string;
  trend?: {
    value: number;
    label: string;
    isPositive?: boolean;
  };
  loading?: boolean;
}

function StatCard({ title, value, icon, color, suffix, trend, loading }: StatCardProps) {
  return (
    <Card
      hoverable
      style={{ height: '100%' }}
      styles={{ body: { padding: 20 } }}
      loading={loading}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <Statistic
            title={
              <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
                {title}
              </span>
            }
            value={value}
            formatter={(val) => formatNumber(val as number)}
            suffix={suffix}
            valueStyle={{ fontSize: 28, fontWeight: 600, color: 'var(--color-text)' }}
          />
          {trend && (
            <Tooltip title={trend.label}>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: trend.isPositive ? 'var(--color-success)' : 'var(--color-error)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {trend.isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                <span>{trend.value > 0 ? `+${formatNumber(trend.value)}` : formatNumber(trend.value)}</span>
                <span style={{ color: 'var(--color-text-tertiary)', marginLeft: 4 }}>
                  {trend.label}
                </span>
              </div>
            </Tooltip>
          )}
        </div>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            backgroundColor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
}

export function StatsCards({ stats, loading }: StatsCardsProps) {
  const cards: StatCardProps[] = [
    {
      title: 'Total Jobs',
      value: stats?.totalJobs ?? 0,
      icon: <DatabaseOutlined />,
      color: '#1677ff',
      trend: stats?.jobsToday
        ? { value: stats.jobsToday, label: 'today', isPositive: true }
        : undefined,
    },
    {
      title: 'Running',
      value: stats?.runningJobs ?? 0,
      icon: <SyncOutlined spin={!!stats?.runningJobs} />,
      color: '#faad14',
    },
    {
      title: 'Completed',
      value: stats?.completedJobs ?? 0,
      icon: <CheckCircleOutlined />,
      color: '#52c41a',
      suffix: stats?.totalJobs
        ? `(${Math.round((stats.completedJobs / stats.totalJobs) * 100)}%)`
        : undefined,
    },
    {
      title: 'Places Found',
      value: stats?.totalPlaces ?? 0,
      icon: <EnvironmentOutlined />,
      color: '#722ed1',
      trend: stats?.placesToday
        ? { value: stats.placesToday, label: 'today', isPositive: true }
        : undefined,
    },
  ];

  return (
    <Row gutter={[16, 16]}>
      {cards.map((card, index) => (
        <Col key={index} xs={24} sm={12} lg={6}>
          <StatCard {...card} loading={loading} />
        </Col>
      ))}
    </Row>
  );
}

export default StatsCards;
